import type { Address } from '#root/types/components/checkout/order'

const ENCODED_USER_PROPERTIES_MAP = [
  ['firstname', 'firstName'],
  ['lastname', 'lastName'],
  ['phone', 'phone'],
  ['zipcode', 'postalCode']
]

export default () => {
  const { minimumLoyaltyPoints } = useAppConfig().components.checkout.paymentLoyalty
  const user = useUserStore()
  const loyalty = useLoyaltyStore()
  const auth = useAuthStore()
  const { includeLoyaltyTierInLoadUserDataEvent } = useAppConfig().components.gtm

  /**
   * Many values needed for analytics are not available immediately on page load.
   * Here we wait for the values to be available before resolving.
   */
  const getUserEventData = async (fallbackUserData?: Partial<Address>, consumerId?: string) => {
    let email: string | undefined
    let loyaltyMemberId = auth.memberId
    try {
      if (!loyaltyMemberId) loyaltyMemberId = await auth.getMemberId()
    }
    catch (err) {
      log.error(`[@domains/commerce/utils/gtm.ts] Error: ${err}`)
    }

    if (auth.loggedIn) {
      await Promise.all([
        !user.details ? user.get() : Promise.resolve(),
        !loyalty.rewardsRequested ? loyalty.getRewards() : until(() => loyalty.rewardsPending).toBe(false),
        loyalty.vouchersStatus === 'idle' ? loyalty.getVouchers() : until(() => loyalty.vouchersPending).toBe(false),
      ])
      email = user.details?.email
    }
    else {
      email = fallbackUserData?.email
    }

    const encodedUserDetails = {}
    if (fallbackUserData) {
      await Promise.all(
        ENCODED_USER_PROPERTIES_MAP.map(async ([key, value]) => {
          let valueToEncode: string | undefined
          if (auth.loggedIn && user.details?.[value]) {
            // detail taken from authenticated customer's profile data
            valueToEncode = user.details[value]
          }
          else if (fallbackUserData?.[value]) {
            // detail provided via override (from billing address) - for guests and authenticated
            // customers with no data provided in the profile
            valueToEncode = fallbackUserData[value]
          }
          if (valueToEncode) {
            encodedUserDetails[`encoded-${key}-sha`] = await generateHashHex(valueToEncode)
          }
        })
      )
    }

    const defaultVouchers = { applicableLoyaltyVouchers: [], currentLoyaltyPoints: 0 }
    const { applicableLoyaltyVouchers, currentLoyaltyPoints } = loyalty.vouchers || defaultVouchers
    const hasAvailableVouchers = applicableLoyaltyVouchers?.some(({ available, eligible }) => available && eligible)
    const hasMinLoyaltyPoints = currentLoyaltyPoints >= (applicableLoyaltyVouchers?.[0]?.points || minimumLoyaltyPoints)

    return {
      ...(loyaltyMemberId && {
        'loyaltyMember-id': loyaltyMemberId,
      }),
      ...(email && {
        'encoded-email': btoa(email),
        'encoded-email-sha': await generateHashHex(email)
      }),
      ...(auth.loggedIn && {
        // if the subscription with the type "Newsletter" has an doubleOptin value as true, we can use this identifier to push data to the data layer https://digital.vfc.com/jira/browse/GLOBAL15-100442
        'nl-subscription': user.hasNewsletterSubscription,
        'rewardsAvailable': loyalty.rewardsAvailable.length > 0 || (hasAvailableVouchers && hasMinLoyaltyPoints),
      }),
      ...(consumerId && {
        'consumer-id': consumerId
      }),
      ...(includeLoyaltyTierInLoadUserDataEvent && user.profile?.loyaltyInfo && {
        loyaltyTier: user.profile.loyaltyInfo.currentTier
      }),
      ...encodedUserDetails
    }
  }

  return {
    getUserEventData,
  }
}
